import React from 'react';

interface CompanyLogo {
  id: string;
  company_name: string;
  logo_url: string;
  website_url: string | null;
}

interface CompanyLogosSectionProps {
  logos: CompanyLogo[];
  title?: string;
  subtitle?: string;
  className?: string;
  /** monochrome = vit inverterad logga (partners/varumärken). color = originalfärger (sponsorer). */
  variant?: 'monochrome' | 'color';
  /** large = större rutor, centrerad flex-rad (endast sponsorer) */
  size?: 'default' | 'large';
}

function DefaultLogoTile({ logo }: { logo: CompanyLogo }) {
  return (
    <div className="group bg-slate-800/25 border border-slate-700 p-8 rounded-none transition-all duration-300 hover:border-slate-600 relative overflow-hidden h-32 flex items-center justify-center">
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-none" />
      <img
        src={logo.logo_url}
        alt={logo.company_name}
        className="max-w-full max-h-full object-contain filter brightness-0 invert opacity-80 group-hover:opacity-100 transition-all duration-300 relative z-10"
        loading="lazy"
      />
    </div>
  );
}

function SponsorLogoTile({ logo }: { logo: CompanyLogo }) {
  return (
    <div className="group relative flex h-40 sm:h-48 md:h-52 w-[240px] sm:w-[280px] max-w-[300px] items-center justify-center overflow-hidden rounded-lg border border-slate-600/30 bg-white p-6 md:p-8 shadow-lg shadow-black/20 transition-all duration-300 hover:border-purple-400/40 hover:shadow-purple-500/10">
      <img
        src={logo.logo_url}
        alt={logo.company_name}
        className="relative z-10 h-24 sm:h-28 md:h-32 w-auto max-w-[88%] object-contain object-center opacity-95 transition-all duration-300 group-hover:scale-[1.03] group-hover:opacity-100"
        loading="lazy"
      />
    </div>
  );
}

const CompanyLogosSection: React.FC<CompanyLogosSectionProps> = ({
  logos,
  title = 'Våra samarbetspartners',
  subtitle = 'Vi är stolta över att arbeta med ledande företag',
  className = '',
  variant = 'monochrome',
  size = 'default',
}) => {
  const isSponsorLayout = variant === 'color' && size === 'large';

  if (logos.length === 0) {
    return null;
  }

  return (
    <div className={`max-w-6xl mx-auto bg-slate-900/35 border border-slate-800 p-12 ${className}`}>
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && (
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-white leading-tight">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
      )}

      <div
        className={
          isSponsorLayout
            ? 'flex flex-wrap justify-center gap-8 md:gap-10'
            : 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 items-center'
        }
      >
        {logos.map((logo) => {
          const content = isSponsorLayout ? (
            <SponsorLogoTile logo={logo} />
          ) : (
            <DefaultLogoTile logo={logo} />
          );

          const wrapperClass = isSponsorLayout
            ? 'group shrink-0 w-[240px] sm:w-[280px] max-w-[300px]'
            : 'group';

          if (logo.website_url) {
            return (
              <a
                key={logo.id}
                href={logo.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className={wrapperClass}
                title={logo.company_name}
              >
                {content}
              </a>
            );
          }

          return (
            <div key={logo.id} title={logo.company_name} className={wrapperClass}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CompanyLogosSection;
